import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import queryString from 'query-string'

import { RecipesSearch } from '@/routes/recipes'
import { SEARCH_API_URL, apiClient } from '.'
import {
  FoodRestriction,
  NewRecipe,
  PhotoModerationEntry,
  PhotoModerationStatus,
  Recipe,
  RecipeSource,
  RecipeTag,
  RecipesStatistics,
  TempRecipe,
  UserRecipePhoto,
} from './types'

export type SearchQuery = {
  page: number
  pageSize?: number
  searchText?: string
  idIngredient?: number
  model?: RecipeSearchModel
}

export type RecipeSearchModel = 'EXTENDED' | 'SIMPLE'

const DEFAULT_PAGE_SIZE = 20

// GET /recipes
export async function searchRecipes(query: SearchQuery) {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE

  return apiClient.get<Recipe[]>(`/recipes`, {
    params: {
      ...query,
      offset: pageSize * query.page,
      name: query.searchText,
      count: pageSize,
      model: query.model ?? 'EXTENDED',
      loadRecipeAvailableLanguages: true,
    },
    baseURL: SEARCH_API_URL,
    paramsSerializer: (params) => {
      return queryString.stringify(params)
    },
  })
}

export const recipesQuery = (query: SearchQuery) => {
  return queryOptions({
    queryKey: ['recipes', query],
    queryFn: () => searchRecipes(query),
    placeholderData: keepPreviousData,
  })
}

export async function getRecipesStats(query: RecipesSearch) {
  return apiClient.get<RecipesStatistics>(`/recipes/stats`, {
    params: {
      ...query,
      model: 'EXTENDED',
    },
    baseURL: SEARCH_API_URL,
    paramsSerializer: (params) => {
      return queryString.stringify(params)
    },
  })
}

export const recipesStatsQuery = (query: RecipesSearch) => {
  return queryOptions({
    queryKey: ['recipes_stats', query],
    queryFn: () => getRecipesStats(query),
    placeholderData: keepPreviousData,
  })
}

// GET /recipe
async function fetchRecipe(recipeId: string): Promise<Recipe> {
  return apiClient.get(`/recipe/${recipeId}`).then((res) => res.data)
}

export const recipeQuery = (recipeId: string) => {
  return queryOptions({
    queryKey: ['recipe', parseInt(recipeId)],
    queryFn: () => fetchRecipe(recipeId),
  })
}

// POST /recipe
export async function saveRecipe(recipe: TempRecipe | NewRecipe) {
  return apiClient.post('/recipe/', recipe).then((res) => res.data)
}

// DELETE /recipe/$id
export async function deleteRecipe(recipeId: number) {
  return apiClient.delete(`/recipe/${recipeId}`)
}

// GET /tag
async function fetchTags(): Promise<RecipeTag[]> {
  return apiClient.get(`/tag`).then((res) => res.data)
}

export const tagsQuery = queryOptions({
  queryKey: ['tags'],
  queryFn: fetchTags,
})

// GET /recipe/generateTags
export async function getGenerateTags(ingId: number[]): Promise<RecipeTag[]> {
  return apiClient
    .get(`/recipe/generateTags`, {
      params: {
        ingId,
      },
      paramsSerializer: (params) => {
        return queryString.stringify(params)
      },
    })
    .then((res) => res.data)
}

export const generateTagsQuery = (ingId: number[]) => {
  return queryOptions({
    queryKey: ['generateTags', ingId],
    queryFn: () => getGenerateTags(ingId),
  })
}

// GET /recipe/food-restrictions
export async function getRecipeFoodRestrictions(ingId: number[]): Promise<FoodRestriction[]> {
  return apiClient
    .get(`/recipe/food-restrictions`, {
      params: {
        ingId,
      },
      paramsSerializer: (params) => {
        return queryString.stringify(params)
      },
    })
    .then((res) => res.data)
}

async function fetchFoodRestrictions(): Promise<FoodRestriction[]> {
  return apiClient.get(`/food-restrictions`).then((res) => res.data)
}

export const foodRestrictionsQuery = queryOptions({
  queryKey: ['foodRestrictions'],
  queryFn: fetchFoodRestrictions,
})

export const sourcesQuery = queryOptions({
  queryKey: ['recipe-source'],
  queryFn: () =>
    apiClient.get('/recipe/recipe-source').then((res) => res.data) as Promise<RecipeSource[]>,
})

// GET /recipe/{id}/photos
async function fetchRecipeThumbnail(idRecipe: number) {
  const photos = await apiClient
    .get<UserRecipePhoto[]>(`/recipes/${idRecipe}/photos`, {
      baseURL: '/API',
    })
    .then((res) => res.data)

  if (photos.length === 0) {
    return null
  }

  // Find cover image or use first photo
  return photos.find((p) => p.isCover) || photos[0]
}

export const recipeThumbnailQuery = (idRecipe: number) =>
  queryOptions({
    queryKey: ['recipeThumbnail', idRecipe],
    queryFn: () => fetchRecipeThumbnail(idRecipe),
  })

export async function fetchRecipePhotos(idRecipe: number): Promise<UserRecipePhoto[]> {
  return apiClient
    .get<UserRecipePhoto[]>(`/recipes/${idRecipe}/photos`, {
      baseURL: '/API',
    })
    .then((res) => res.data)
}

export const recipePhotosQuery = (idRecipe: number) =>
  queryOptions({
    queryKey: ['recipePhotos', idRecipe],
    queryFn: () => fetchRecipePhotos(idRecipe),
  })

// POST /recipe/{id}/photos
export function uploadPhoto(idRecipe: number, formData: FormData) {
  return apiClient.post<UserRecipePhoto[]>(`/recipes/${idRecipe}/photos`, formData, {
    baseURL: '/API',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

// DELETE /recipes/photos
export function deletePhoto(idRecipePhoto: number) {
  return apiClient.delete('/recipes/photos', {
    baseURL: '/API',
    data: [idRecipePhoto],
  })
}

export async function downloadExportZip(): Promise<AxiosResponse> {
  return apiClient.get(`/recipe/export`, {
    responseType: 'blob',
  })
}

export async function fetchPhotosModeration(status: PhotoModerationStatus = 'UNDER_REVIEW') {
  return apiClient
    .get<PhotoModerationEntry[]>('/recipes/photos/moderation', {
      baseURL: '/API',
      params: {
        status,
      },
    })
    .then((res) => res.data)
}

export const photosModerationQuery = () =>
  queryOptions({
    queryKey: ['photosModeration'],
    queryFn: () => fetchPhotosModeration(),
  })

export async function fetchPhotoModeration(idModeration: number) {
  return apiClient
    .get<PhotoModerationEntry>(`/recipes/photos/moderation/${idModeration}`, {
      baseURL: '/API',
    })
    .then((res) => res.data)
}

export const photoModerationQuery = (idModeration: number) =>
  queryOptions({
    queryKey: ['photoModeration', idModeration],
    queryFn: () => fetchPhotoModeration(idModeration),
  })

export async function approvePhoto(idRecipePhoto: number) {
  return apiClient.post(`/recipes/photos/${idRecipePhoto}/moderation/approve`, undefined, {
    baseURL: '/API',
  })
}

export async function rejectPhoto(idRecipePhoto: number) {
  return apiClient.post(
    `/recipes/photos/${idRecipePhoto}/moderation/reject`,
    {},
    {
      baseURL: '/API',
    },
  )
}

export async function setAsCover(idRecipePhoto: number) {
  return apiClient.post(
    `/recipes/photos/${idRecipePhoto}/cover`,
    {},
    {
      baseURL: '/API',
    },
  )
}

export async function fetchAIImagePrompt(idRecipe: number) {
  return apiClient
    .get<string>(`/recipes/${idRecipe}/generate-image-ai-prompt`, {
      baseURL: '/API',
    })
    .then((res) => res.data)
}

export const aiImagePromptQuery = (idRecipe: number) =>
  queryOptions({
    queryKey: ['aiImagePrompt', idRecipe],
    queryFn: () => fetchAIImagePrompt(idRecipe),
  })

export async function generateAIImage(idRecipe: number, additionalInstructions: string) {
  return apiClient
    .post<Blob>(
      `/recipes/${idRecipe}/generate-image`,
      {
        additionalInstructions,
      },
      {
        baseURL: '/API',
        responseType: 'blob',
      },
    )
    .then((res) => res.data)
}
